import type { AnnualRefund, AnnualSummary, PersonalDeductionItem, RefundMilestone } from '../model/period';
import { formatDayShort, formatPesos } from './formatters';

export type RefundMood = 'neutro' | 'accion' | 'urgente' | 'positivo' | 'mixto' | 'verde' | 'gris';

const STATE_MOOD: Record<string, RefundMood> = {
	en_proceso: 'neutro',
	revision_clabe: 'neutro',
	inconsistente_clabe: 'accion',
	creditos_fiscales: 'neutro',
	requerida: 'urgente',
	autorizada: 'positivo',
	autorizada_parcial: 'mixto',
	pago_en_proceso: 'positivo',
	pagada: 'verde',
	improcedente: 'accion',
	negada: 'gris',
	desistida: 'gris',
};

export const REFUND_STEP_LABEL: Record<string, string> = {
	en_proceso: 'En revisión',
	revision_clabe: 'Validando tu cuenta CLABE',
	inconsistente_clabe: 'Cuenta CLABE sin validar',
	creditos_fiscales: 'Revisando un adeudo',
	requerida: 'Te pidieron información',
	autorizada: 'Autorizada',
	autorizada_parcial: 'Autorizada en parte',
	pago_en_proceso: 'Depósito ordenado',
	pagada: 'Depositada',
	improcedente: 'No procedió',
	negada: 'Negada',
	desistida: 'Desistida',
};

export const refundStepLabel = (state: string) => REFUND_STEP_LABEL[state] ?? 'En revisión';

export const REFUND_KIND_LABEL: Record<string, string> = {
	automatica: 'Devolución automática',
	manual: 'Solicitud de devolución (FED)',
	compensacion: 'Compensación',
	acreditamiento: 'Acreditamiento',
};

export type RefundPresentation = {
	mood: RefundMood;
	title: string;
	message?: string;
	kindLabel?: string;
	amount?: { label: string; value: number };
	facts: { label: string; value: string }[];
	updatedText?: string;
	helpMessage?: string;
};

const stateTitle = (refund: AnnualRefund): { title: string; message?: string } => {
	switch (refund.state) {
		case 'en_proceso':
			return refund.kind === 'manual'
				? { title: 'El SAT está revisando tu solicitud de devolución', message: FED_TERM }
				: { title: 'El SAT está revisando tu devolución' };
		case 'revision_clabe':
			return { title: 'El SAT está validando tu cuenta CLABE' };
		case 'inconsistente_clabe':
			return { title: 'El SAT no pudo validar tu cuenta CLABE', message: 'Hace falta tu estado de cuenta.' };
		case 'creditos_fiscales':
			return { title: 'El SAT detectó un adeudo pendiente y lo está revisando' };
		case 'requerida':
			return {
				title: 'El SAT te pide información',
				message:
					'El plazo estimado es de 20 días hábiles si es el primer requerimiento y de 10 si es el segundo, contados desde el día hábil siguiente a la notificación.',
			};
		case 'autorizada':
			return { title: 'Tu devolución está autorizada', message: 'Falta que el SAT haga el depósito; damos seguimiento constante al estatus.' };
		case 'autorizada_parcial':
			return {
				title:
					refund.resolved !== undefined && refund.requested !== undefined
						? `El SAT autorizó una parte: ${formatPesos(refund.resolved)} de ${formatPesos(refund.requested)}`
						: 'El SAT autorizó una parte de tu devolución',
			};
		case 'pago_en_proceso':
			return { title: 'El SAT ya ordenó el depósito' };
		case 'pagada':
			return {
				title: [
					'El SAT te depositó',
					refund.paid !== undefined ? formatPesos(refund.paid) : undefined,
					refund.paidOn ? `el ${formatDayShort(refund.paidOn)}` : undefined,
				]
					.filter(Boolean)
					.join(' '),
			};
		case 'improcedente':
			return { title: 'El SAT no aceptó la devolución automática', message: 'Se puede pedir con una solicitud de devolución (FED).' };
		case 'negada':
			return { title: 'El SAT negó la solicitud' };
		case 'desistida':
			return { title: 'La solicitud se dio por desistida' };
		default:
			return { title: 'El SAT está revisando tu devolución' };
	}
};

const FED_TERM = 'Por ley, el SAT tiene 40 días hábiles para resolverla.';

const autoRejectedOf = (refund: AnnualRefund) =>
	(refund.timeline ?? []).some((step) => step.kind === 'automatica' && step.event === 'resultado' && step.state === 'improcedente');

const fedContextOf = (refund: AnnualRefund) => {
	if (refund.case !== 'fed' || !refund.fed || !autoRejectedOf(refund)) return undefined;
	return `Como el SAT no aceptó la devolución automática, se pidió con una solicitud (FED) el ${formatDayShort(refund.fed.presentedOn)}.`;
};

const amountOf = (refund: AnnualRefund) => {
	if (refund.state === 'pagada' && refund.paid !== undefined) return { label: 'Te depositó', value: refund.paid };
	if (refund.resolved !== undefined && refund.state?.startsWith('autorizada')) return { label: 'Autorizado', value: refund.resolved };
	if (refund.requested !== undefined) return { label: 'Pediste', value: refund.requested };
	return undefined;
};

const helpOf = (refund: AnnualRefund, year: number) => {
	switch (refund.state) {
		case 'inconsistente_clabe':
			return `Hola, el SAT no pudo validar mi cuenta CLABE en la devolución de mi anual ${year}.`;
		case 'requerida':
			return `Hola, el SAT me pide información para la devolución de mi anual ${year}.`;
		case 'improcedente':
			return `Hola, el SAT no aceptó la devolución automática de mi anual ${year} y quiero pedirla con una solicitud.`;
		default:
			return undefined;
	}
};

export const presentRefund = (refund: AnnualRefund, year: number, inFavor?: number): RefundPresentation => {
	const fedWhen = refund.fed
		? `${formatDayShort(refund.fed.presentedOn)}${refund.fed.requested !== undefined ? ` por ${formatPesos(refund.fed.requested)}` : ''}`
		: undefined;
	if (!refund.state) {
		const favorText = inFavor !== undefined ? ` de ${formatPesos(inFavor)}` : '';
		switch (refund.case) {
			case 'compensacion':
				return {
					mood: 'neutro',
					title: `Elegiste compensar tu saldo a favor${favorText}`,
					message: 'Se aplica en tus próximas declaraciones.',
					facts: [],
				};
			case 'sin_seleccion':
				return { mood: 'accion', title: `Tu declaración no pidió la devolución${favorText}`, facts: [] };
			case 'fed':
				return {
					mood: 'neutro',
					title: fedWhen ? `Solicitud de devolución presentada el ${fedWhen}` : 'Presentaste una solicitud de devolución',
					message: 'Está en espera de que el SAT la registre.',
					kindLabel: REFUND_KIND_LABEL.manual,
					facts: [],
				};
			default:
				return {
					mood: 'neutro',
					title: `El SAT abrirá tu devolución automática${favorText}`,
					message: 'Te avisamos en cuanto la registre.',
					kindLabel: REFUND_KIND_LABEL.automatica,
					facts: [],
				};
		}
	}
	const { title, message } = stateTitle(refund);
	const facts = refund.clabeLast4 ? [{ label: 'Cuenta', value: `···${refund.clabeLast4}` }] : [];
	return {
		mood: STATE_MOOD[refund.state] ?? 'neutro',
		title,
		message: [fedContextOf(refund), message].filter(Boolean).join(' ') || undefined,
		kindLabel: refund.kind ? REFUND_KIND_LABEL[refund.kind] : undefined,
		amount: amountOf(refund),
		facts,
		updatedText: refund.updatedAt ? `Actualizado el ${formatDayShort(refund.updatedAt)}` : undefined,
		helpMessage: helpOf(refund, year),
	};
};

export type RefundStepTone = 'hecho' | 'rechazo' | 'atencion' | 'en_curso' | 'gris';

export type RefundStep = {
	key: string;
	tone: RefundStepTone;
	title: string;
	date?: string;
	amount?: number;
	lines: string[];
	note?: string;
	finding?: RefundMilestone['finding'];
	showAsk: boolean;
	fedDocument: boolean;
	deposit: boolean;
};

const STEP_TONE: Record<string, RefundStepTone> = {
	pagada: 'hecho',
	autorizada: 'hecho',
	pago_en_proceso: 'hecho',
	improcedente: 'rechazo',
	negada: 'rechazo',
	requerida: 'atencion',
	inconsistente_clabe: 'atencion',
	creditos_fiscales: 'atencion',
	autorizada_parcial: 'atencion',
	desistida: 'gris',
};

const resultTitleOf = (step: RefundMilestone) => {
	const manual = step.kind === 'manual';
	switch (step.state) {
		case 'pagada':
			return 'El SAT te depositó';
		case 'improcedente':
			return manual ? 'El SAT no aceptó la solicitud' : 'El SAT no aceptó la devolución automática';
		case 'negada':
			return 'El SAT negó la solicitud';
		case 'desistida':
			return 'La solicitud se dio por desistida';
		case 'requerida':
			return 'El SAT te pidió información';
		case 'autorizada':
			return 'El SAT autorizó tu devolución';
		case 'autorizada_parcial':
			return 'El SAT autorizó una parte';
		case 'pago_en_proceso':
			return 'El SAT ordenó el depósito';
		case 'inconsistente_clabe':
			return 'El SAT no pudo validar tu cuenta CLABE';
		case 'revision_clabe':
			return 'El SAT está validando tu cuenta CLABE';
		case 'creditos_fiscales':
			return 'El SAT está revisando un adeudo';
		default:
			return manual ? 'El SAT está revisando tu solicitud' : 'El SAT está revisando tu devolución automática';
	}
};

const listText = (items: string[]) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} y ${items.at(-1)}`);

export const refundStepsOf = (refund: AnnualRefund): RefundStep[] => {
	const timeline = refund.timeline ?? [];
	const lastFed = timeline.filter((step) => step.event === 'solicitud').at(-1);
	return timeline.map((step, index) => {
		const key = `${step.kind}-${step.event}-${step.folio ?? index}`;
		const base = { key, date: step.on || undefined, amount: step.amount, lines: [], showAsk: false, fedDocument: false, deposit: false };
		if (step.event === 'pedida') return { ...base, tone: 'hecho', title: 'Pediste tu saldo a favor en la anual' };
		if (step.event === 'solicitud') {
			const fed = step === lastFed ? refund.fed : undefined;
			return {
				...base,
				tone: 'hecho',
				title: 'Se presentó tu solicitud de devolución (FED)',
				lines: [
					step.folio && `Folio ${step.folio}`,
					fed?.office && `La revisa la ${fed.office}`,
					fed && fed.attachments.length > 0 && `Se mandó con ${listText(fed.attachments.map((item) => item.toLowerCase()))}`,
				].filter((line): line is string => Boolean(line)),
				fedDocument: step === lastFed,
			};
		}
		const last = index === timeline.length - 1;
		return {
			...base,
			tone: STEP_TONE[step.state ?? ''] ?? 'en_curso',
			title: resultTitleOf(step),
			note: step.kind === 'manual' && last && (step.state === 'en_proceso' || !step.state) ? FED_TERM : undefined,
			finding: step.finding,
			showAsk: last,
			deposit: step.state === 'pagada',
		};
	});
};

export const refundStatusLabel = (refund: NonNullable<AnnualSummary['refundStatus']>) => {
	if (refund.state === 'pagada')
		return refund.amount !== undefined ? `El SAT te depositó ${formatPesos(refund.amount)}` : 'El SAT ya te depositó';
	return `Devolución: ${refundStepLabel(refund.state).toLowerCase()}`;
};

const RUBRO_LABEL: Record<string, string> = {
	honorarios_medicos: 'Médicos y dentistas',
	colegiaturas: 'Colegiaturas',
	hipotecario: 'Crédito hipotecario',
	donativos: 'Donativos',
	seguro_gastos_medicos: 'Seguro de gastos médicos',
	aportaciones_retiro: 'Aportaciones para tu retiro',
	funerales: 'Gastos funerarios',
	ahorro: 'Cuentas de ahorro',
	transporte_escolar: 'Transporte escolar',
};

export const rubroLabel = (item: Pick<PersonalDeductionItem, 'rubro' | 'rubroSat'>) =>
	RUBRO_LABEL[item.rubro] ?? item.rubroSat ?? 'Sin clasificar';

const CFDI_USE_RUBRO: Record<string, string> = {
	D01: 'honorarios_medicos',
	D02: 'honorarios_medicos',
	D03: 'funerales',
	D04: 'donativos',
	D05: 'hipotecario',
	D06: 'aportaciones_retiro',
	D07: 'seguro_gastos_medicos',
	D08: 'transporte_escolar',
	D09: 'ahorro',
	D10: 'colegiaturas',
};

export const cfdiUseLabel = (use: string) => RUBRO_LABEL[CFDI_USE_RUBRO[use]] ?? 'Deducción personal';

const REASON_COPY: Record<string, string> = {
	medicamento_sin_hospital: 'las medicinas solo cuentan si las factura el hospital junto con la atención',
	descripcion_no_corresponde: 'la descripción de la factura no dice que sea un servicio médico',
	clave_prodserv_invalida: 'la clave de producto de la factura no es de este tipo de gasto',
	uso_cfdi_incorrecto: 'la factura se pidió con un uso de CFDI que no es el de deducción personal',
	forma_pago_no_bancarizada: 'se pagó en efectivo; solo cuentan pagos con tarjeta, transferencia o cheque',
	complemento_educativo_incompleto: 'a la factura de la escuela le falta el complemento (RVOE, nivel o CURP)',
	emisor_fuera_de_padron: 'la escuela no está en el padrón del SAT',
	emisor_inconsistente: 'el SAT tiene observaciones sobre quien emitió la factura',
	limite_lentes: 'los lentes solo se deducen hasta $2,500',
	mas_de_un_credito_hipotecario: 'solo se deduce un crédito hipotecario',
	cfdi_duplicado: 'el SAT ya tiene otra factura igual',
};

export const deductionNoteOf = (item: PersonalDeductionItem) => {
	if (item.verdict === 'aceptada') return 'El SAT ya la tomó';
	if (item.verdict === 'no_clasificada') return 'El SAT no la clasificó; conviene revisarla en la anual';
	const reasons = item.reasons
		.map((reason) => (reason === 'otro' ? item.observations.join(' ') : REASON_COPY[reason]))
		.filter((text): text is string => Boolean(text));
	return reasons.length > 0 ? `El SAT no la tomará: ${reasons.join('; ')}` : 'El SAT no la tomará';
};
